/**
 * The guest-conversion hand-off — what a fan carried with them, encoded for the
 * trip back across the app boundary.
 *
 * "Conversion", not "claim". A **claim** is an ownership transfer: a Stub entity
 * becoming a workspace someone owns (`EntityClaim` / `claimService`, and the
 * only sense CONTEXT.md defines). This is a *person's* ephemeral session
 * becoming a registered account, which creates no workspace at all. The two
 * used to share a word, and sat one line apart in `AdminOnboarding` with
 * opposite preconditions.
 *
 * A guest upgrades their account on the **admin** origin (`/register?convert=…`)
 * and is bounced back to the **live** origin to rejoin the room. Those are two
 * different origins, so `sessionStorage` doesn't cross and there is no shared
 * client state at all — the only channel is the URL. That's why this is a
 * codec rather than a plain object handed between components.
 *
 * Both halves live here on purpose. When the encoder and the decoder sat on
 * either side of an origin boundary as loose `params.get('cheer')` calls, a
 * renamed key would have been a silent failure: the fan lands in the room and
 * simply sees nothing, which is indistinguishable from the bug this whole flow
 * exists to fix. One module, one set of keys, unit-tested.
 *
 * Pure — no DOM, no network. Safe to import anywhere.
 */

/** Currency types a room can award into. Mirrors the backend's snapshot types. */
export type ConvertCurrencyType = 'venue' | 'performer';

/**
 * What the conversion moved into the account. Shape matches the `claimed` field
 * on `POST /api/auth/convert` (see `userService.GuestConvertSummary`).
 */
export interface GuestConvertSummary {
  /** False when the conversion landed in an account that already existed. */
  newAccount: boolean;
  /** Totals by currency type. A type with nothing carried is absent, not 0. */
  currencies: Partial<Record<ConvertCurrencyType, number>>;
  /** Store items carried across. */
  items: number;
}

/**
 * The marker param. Its presence alone is what says "a conversion just
 * completed".
 *
 * Still spelled `claimed` on the wire, deliberately. These keys are a contract
 * between two SEPARATELY DEPLOYED apps — admin encodes, live decodes — so a
 * rename is live during the window where one has shipped and the other hasn't,
 * and it fails exactly the way this module was written to prevent: the fan
 * lands in the room, sees nothing, and that is indistinguishable from the bug
 * the whole flow exists to fix. Renaming the symbols costs nothing; renaming
 * the wire costs a silent failure for no gain.
 */
const FLAG = 'claimed';

/**
 * Short keys, because this URL is briefly visible to a fan on their phone.
 * Deliberately not the canon currency names — those are display copy and are
 * free to change; these are a wire format and are not.
 */
const CURRENCY_PARAM: Record<ConvertCurrencyType, string> = {
  performer: 'c',
  venue: 'h',
};

/** Every param this codec owns, so the reader can strip exactly its own. */
export const GUEST_CONVERT_PARAMS = [FLAG, 'n', 'i', ...Object.values(CURRENCY_PARAM)] as const;

/**
 * Encode a summary as query params.
 *
 * Zero-valued fields are omitted rather than serialised as `0` — the decoder
 * treats absent and zero identically, and a shorter URL is one less thing for
 * the fan to see flash past.
 */
export function encodeGuestConvert(summary: GuestConvertSummary): URLSearchParams {
  const params = new URLSearchParams();
  params.set(FLAG, '1');
  if (summary.newAccount) params.set('n', '1');
  for (const [type, key] of Object.entries(CURRENCY_PARAM) as [ConvertCurrencyType, string][]) {
    const amount = summary.currencies[type];
    if (amount && amount > 0) params.set(key, String(Math.round(amount)));
  }
  if (summary.items > 0) params.set('i', String(summary.items));
  return params;
}

/**
 * Decode a summary from query params, or `null` when this isn't a claim landing.
 *
 * Every numeric field is parsed defensively and falls back to nothing: these
 * values arrive in a URL the fan can edit, and the worst outcome of a garbled
 * one should be a confirmation with no numbers, never a `NaN` on screen.
 */
export function decodeGuestConvert(search: string | URLSearchParams): GuestConvertSummary | null {
  const params = typeof search === 'string' ? new URLSearchParams(search) : search;
  if (params.get(FLAG) !== '1') return null;

  const num = (key: string): number | undefined => {
    const raw = params.get(key);
    if (raw === null) return undefined;
    const parsed = Number.parseInt(raw, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
  };

  const currencies: GuestConvertSummary['currencies'] = {};
  for (const [type, key] of Object.entries(CURRENCY_PARAM) as [ConvertCurrencyType, string][]) {
    const amount = num(key);
    if (amount !== undefined) currencies[type] = amount;
  }

  return {
    newAccount: params.get('n') === '1',
    currencies,
    items: num('i') ?? 0,
  };
}

/** True when the claim carried nothing — a fan who signed up on their way in. */
export function convertCarriedNothing(summary: GuestConvertSummary): boolean {
  return summary.items === 0 && Object.keys(summary.currencies).length === 0;
}
